import React, { useState } from 'react';
import { Alert, Box, Typography, useMediaQuery, IconButton, Collapse, Tooltip, Link } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import round from '../../../helpers/round';
import StroudF1GraphSvg from './StroudF1GraphSvg';
import StroudF2GraphSvg from './StroudF2GraphSvg';

const StroudResult = ({ result }) => {
  const { pi, spt } = result.inputs;
  const { f1, f2, cu, mv, canUserSeeCalculationSteps } = result.outputs;
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const piValue = Number(pi);
  const f1Value = Number(f1);
  const f2Value = Number(f2);

  const isPIForf1_OK = piValue >= 15 && piValue <= 64;
  const isPIForf2_OK = piValue >= 12 && piValue <= 65;

  const StroudF1Result = () => {
    const [showF1Details, setShowF1Details] = useState(false);
    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            c<sub>u</sub> vs SPT-PI Relationship (Stroud)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, textAlign: 'justify', mb: 2 }}>
            Stroud (1974) proposed a correlation between the plasticity index (PI) and the undrained shear strength factor f
            <sub>1</sub>. The chart below is used to determine f<sub>1</sub> based on the input PI, which is then multiplied by
            N<sub>(60)</sub> to estimate the undrained shear strength c<sub>u</sub>.
          </Typography>
          <Box sx={{ width: '100%', maxWidth: 380, mx: 'auto', mb: 2 }}>
            <StroudF1GraphSvg pi={piValue} f1={f1Value} showOverlay={isPIForf1_OK} />
          </Box>
          {!isPIForf1_OK && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Plasticity Index (PI) provided ({round(piValue, 2)}%) is out of the limits of the graph. Results may not be
                accurate. For f<sub>1</sub> valid PI values range between 15% - 64%.
              </Typography>
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowF1Details((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showF1Details ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  c<sub>u</sub>
                </b>{' '}
                (undrained shear strength): {round(cu, 2)} kN/m<sup>2</sup>
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showF1Details} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    For PI = {round(piValue, 2)}% &rarr; f<sub>1</sub> = {round(f1Value, 2)} kN/m<sup>2</sup>{' '}
                    <span>(selected from the graph)</span>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    c<sub>u</sub> = f<sub>1</sub> x N<sub>(60)</sub> = {round(f1Value, 2)} x {round(spt, 2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    c<sub>u</sub> = {round(cu, 2)} kN/m<sup>2</sup>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  const StroudF2Result = () => {
    const [showF2Details, setShowF2Details] = useState(false);
    return (
      <Box sx={{ my: 2, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
            m<sub>v</sub> vs SPT-PI Relationship (Stroud)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, textAlign: 'justify', mb: 2 }}>
            Stroud (1974) also proposed a correlation between PI and the volumetric compressibility factor f<sub>2</sub>. The
            chart below is used to determine f<sub>2</sub> based on the input PI, which is then used with N<sub>(60)</sub> to
            estimate the coefficient of volume compressibility m<sub>v</sub>.
          </Typography>
          <Box sx={{ width: '100%', maxWidth: 380, mx: 'auto', mb: 2 }}>
            <StroudF2GraphSvg pi={piValue} f2={f2Value} showOverlay={isPIForf2_OK} />
          </Box>
          {!isPIForf2_OK && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Plasticity Index (PI) provided ({round(piValue, 2)}%) is out of the limits of the graph. Results may not be
                accurate. For f<sub>2</sub> valid PI values range between 12% - 65%.
              </Typography>
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start',
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24,
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' },
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowF2Details((v) => !v)}
                    sx={{ width: 32, height: 32 }}
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showF2Details ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2" color="text.secondary">
                <b>
                  m<sub>v</sub>
                </b>{' '}
                (coefficient of volume compressibility): {round(mv, 8)} m<sup>2</sup>/kN
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showF2Details} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    For PI = {round(piValue, 2)}% &rarr; f<sub>2</sub> = {round(f2Value, 2)} kN/m<sup>2</sup>{' '}
                    <span>(selected from the graph)</span>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    m<sub>v</sub> = 1 / (f<sub>2</sub> x N<sub>(60)</sub>) = 1 / ({round(f2Value, 2)} x {round(spt, 2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    m<sub>v</sub> = {round(mv, 8)} m<sup>2</sup>/kN
                  </Typography>
                  {spt === 0 ? (
                    <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Please provide a non-zero spt value.
                      </Typography>
                    </Alert>
                  ) : null}
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  return (
    <section className="output stroudOutput">
      <h2>Output </h2>
      <StroudF1Result />
      <StroudF2Result />
    </section>
  );
};

export default React.memo(StroudResult);
